/**
 * Auth Context
 * 
 * Provides authentication state and methods throughout the app.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode
} from 'react';
import {
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  deleteUser,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { doc, getDoc, getDocFromServer, setDoc, deleteDoc, collection, getDocs, writeBatch, onSnapshot as onDocSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '@/firebase/config';
import { UserProfile, createProfile } from '@/types';

interface AuthContextType {
  // State
  user: User | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isOptimistic: boolean; // True when using cached auth (show skeletons for data)

  // Auth methods
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  deleteAccount: () => Promise<void>;

  // Profile methods
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;

  // Utilities
  /** Resolves with the Firebase User once onAuthStateChanged fires. Returns null if not authenticated. */
  waitForUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Auth state cache key for optimistic rendering
const AUTH_CACHE_KEY = 'tp_auth_status';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  // Use cached auth status for faster initial render
  const [isLoading, setIsLoading] = useState(() => {
    // If we have cached auth status, don't block initial render
    const cached = localStorage.getItem(AUTH_CACHE_KEY);
    return cached !== 'authenticated';
  });
  const [isOptimistic, setIsOptimistic] = useState(() => {
    // Optimistic state: we think user is authenticated based on cache
    return localStorage.getItem(AUTH_CACHE_KEY) === 'authenticated';
  });

  // Promise that resolves once onAuthStateChanged first fires.
  // Used by waitForUser() to let consumers await real auth state.
  const [authResolvedPromise] = useState(() => {
    let resolve: (user: User | null) => void;
    const promise = new Promise<User | null>((r) => { resolve = r; });
    return { promise, resolve: resolve! };
  });

  // waitForUser: if user is already resolved, return immediately; else wait for onAuthStateChanged
  const waitForUser = useCallback(async (): Promise<User | null> => {
    if (!isOptimistic && user !== undefined) {
      // Auth already resolved
      return user;
    }
    return authResolvedPromise.promise;
  }, [user, isOptimistic, authResolvedPromise]);

  // Listen to auth state changes + real-time profile listener
  useEffect(() => {
    let unsubProfile: (() => void) | null = null;
    let resolved = false;

    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setIsOptimistic(false); // Auth resolved, no longer optimistic

      // Resolve the waitForUser promise on first auth callback
      if (!resolved) {
        resolved = true;
        authResolvedPromise.resolve(firebaseUser);
      }

      // Tear down previous profile listener
      unsubProfile?.();
      unsubProfile = null;

      if (firebaseUser) {
        // Cache auth status for next visit
        localStorage.setItem(AUTH_CACHE_KEY, 'authenticated');
        await ensureProfileExists(firebaseUser.uid, firebaseUser.email!);

        // Server-first profile read — bypasses IndexedDB cache so count-driven
        // UI (bell badge, hero pill) reflects server truth on first paint.
        const profileRef = doc(db, 'users', firebaseUser.uid);
        getDocFromServer(profileRef)
          .then(fresh => { if (fresh.exists()) setProfile(fresh.data() as UserProfile); })
          .catch(() => { /* fall through to onSnapshot below */ });

        // Real-time profile listener — picks up server-side writes
        unsubProfile = onDocSnapshot(profileRef, (snap) => {
          if (snap.exists()) {
            setProfile(snap.data() as UserProfile);
          }
        });
      } else {
        // Clear cache on sign out
        localStorage.removeItem(AUTH_CACHE_KEY);
        setProfile(null);
      }

      setIsLoading(false);
    });

    return () => {
      unsubAuth();
      unsubProfile?.();
    };
  }, []);

  // Ensure profile document exists (create if new user)
  const ensureProfileExists = async (uid: string, email: string) => {
    try {
      const profileRef = doc(db, 'users', uid);
      const profileSnap = await getDoc(profileRef);

      if (!profileSnap.exists()) {
        // Create new profile — onSnapshot listener will pick it up
        const newProfile = createProfile(uid, email);
        await setDoc(profileRef, newProfile);
      }
    } catch (error) {
      console.error('Error ensuring profile exists:', error);
    }
  };
  
  // Sign up
  const signUp = async (email: string, password: string) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    await ensureProfileExists(credential.user.uid, email);
  };
  
  // Sign in
  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  // Sign in with Google — native on Capacitor, popup on web
  const signInWithGoogle = async () => {
    if (Capacitor.isNativePlatform()) {
      const result = await FirebaseAuthentication.signInWithGoogle();
      const idToken = result.credential?.idToken;
      if (!idToken) throw new Error('Google sign-in failed: no ID token returned');
      const credential = GoogleAuthProvider.credential(idToken);
      const userCredential = await signInWithCredential(auth, credential);
      await ensureProfileExists(userCredential.user.uid, userCredential.user.email!);
    } else {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      const result = await signInWithPopup(auth, provider);
      await ensureProfileExists(result.user.uid, result.user.email!);
    }
  };

  // Sign in with Apple — native on Capacitor, popup on web
  const signInWithApple = async () => {
    if (Capacitor.isNativePlatform()) {
      const result = await FirebaseAuthentication.signInWithApple();
      const idToken = result.credential?.idToken;
      const nonce = result.credential?.nonce;
      if (!idToken) throw new Error('Apple sign-in failed: no ID token returned');
      const oauthProvider = new OAuthProvider('apple.com');
      const credential = oauthProvider.credential({ idToken, rawNonce: nonce });
      const userCredential = await signInWithCredential(auth, credential);
      await ensureProfileExists(userCredential.user.uid, userCredential.user.email!);
    } else {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const result = await signInWithPopup(auth, provider);
      await ensureProfileExists(result.user.uid, result.user.email!);
    }
  };

  // Sign out
  const signOut = async () => {
    await firebaseSignOut(auth);
    setProfile(null);
  };
  
  // Reset password
  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };
  
  // Update profile
  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user || !profile) return;
    
    const updatedProfile = {
      ...profile,
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    
    try {
      const profileRef = doc(db, 'users', user.uid);
      await setDoc(profileRef, updatedProfile, { merge: true });
      setProfile(updatedProfile);
    } catch (error) {
      console.error('Error updating profile:', error);
      // Update local state anyway
      setProfile(updatedProfile);
    }
  };
  
  // Refresh profile from Firestore
  const refreshProfile = async () => {
    if (!user) return;
    await ensureProfileExists(user.uid, user.email!);
  };
  
  // Delete account — server-side via Cloud Function
  // The Cloud Function has admin privileges to:
  // - Cancel Stripe subscriptions
  // - Delete ALL subcollections (including server-only ones like quotes, plaid)
  // - Delete the profile document
  // - Delete the Firebase Auth user
  const deleteAccount = async () => {
    if (!user) {
      throw new Error('No authenticated user');
    }

    try {
      const deleteAccountFn = httpsCallable(functions, 'deleteAccount');
      await deleteAccountFn();

      // Clear local state (auth listener will also fire, but be explicit)
      localStorage.removeItem(AUTH_CACHE_KEY);
      setUser(null);
      setProfile(null);

    } catch (error: any) {
      // Cloud Function errors come wrapped in a FirebaseError
      const code = error?.code || '';
      const message = error?.message || '';

      if (code === 'functions/unauthenticated' || message.includes('requires-recent-login')) {
        throw new Error('Session expired. Sign out and sign back in, then try again.');
      }

      // Surface the Cloud Function error message if available
      throw new Error(
        error?.details?.message || message || 'Account deletion failed. Contact support@trueprice.art.'
      );
    }
  };
  
  const value: AuthContextType = {
    user,
    profile,
    isLoading,
    isAuthenticated: !!user || isOptimistic,
    isOptimistic,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signOut,
    resetPassword,
    deleteAccount,
    updateProfile,
    refreshProfile,
    waitForUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
